import { useEffect, useCallback } from 'react';
import {
	ESCAPE_WHITELIST_TESTIDS,
	ATLASKIT_PORTAL_CLASSNAME,
	ESCAPE_WHITELIST_IDS,
} from '../../../common/constants';
import { CROSS_FLOW } from '../../../common/constants/source';
import { redirectToProjectTemplatesScreen } from '../../../common/utils';
import { useMigrationId, useSource, useThirdPartyState } from '../../import-state/selectors';
import { useCancelImportModalState } from '../../ui-state/selectors';

type UsePreventCancelImport = {
	isCancelImportModalOpen: boolean;
	closeCancelImportModal: () => void;
};

export const usePreventCancelImport = (): UsePreventCancelImport => {
	const [{ isCancelImportModalOpen }, { setCancelImportModalState, setIsCancelImportModalOpen }] =
		useCancelImportModalState();

	const [{ selectedWorkspaceId, selectedEntityId }] = useThirdPartyState();
	const [source] = useSource();
	const [migrationId] = useMigrationId();
	const shouldPreventCancelImport =
		migrationId != null || selectedWorkspaceId != null || selectedEntityId != null;

	const handleKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				// if the event target is whitelisted close the event target
				if (
					event.target instanceof HTMLElement &&
					(ESCAPE_WHITELIST_TESTIDS.some(
						(testId) =>
							event.target instanceof HTMLElement &&
							(event?.target?.dataset?.testid?.includes(testId) ||
								event?.target?.parentElement?.dataset?.testid?.includes(testId)),
					) ||
						ESCAPE_WHITELIST_IDS.some((id) =>
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							(event.target as HTMLElement).id?.includes(id),
						) ||
						event?.target?.parentElement?.className?.includes(ATLASKIT_PORTAL_CLASSNAME) ||
						event?.target?.nodeName === 'INPUT')
				) {
					event.preventDefault();
					event.stopPropagation();
					// close the dropdown menu by clicking the document body
					document.body.click();
					return;
				}
				// prevent closing the project create drawer and exiting import
				if (shouldPreventCancelImport) {
					event.preventDefault();
					event.stopPropagation();

					// Remove this when the new project create integration is implemented
					source === CROSS_FLOW
						? setCancelImportModalState({
								isCancelImportModalOpen: true,
								shouldCloseDrawerOnCancel: false,
								onCloseModalCallback: redirectToProjectTemplatesScreen,
							})
						: setIsCancelImportModalOpen(true);
				}
			}
		},
		[setCancelImportModalState, setIsCancelImportModalOpen, shouldPreventCancelImport, source],
	);

	const closeCancelImportModal = useCallback(() => {
		setIsCancelImportModalOpen(false);
	}, [setIsCancelImportModalOpen]);

	useEffect(() => {
		document.addEventListener('keydown', handleKeyDown);
		let initOverscrollBehaviorX = '';

		// swiping the mapping table horizontally can accidentally result in swiping back in browser history
		// due to OS settings.
		// this will wipe the user's import progress and close the project create drawer
		// add `overscroll-behavior-x: contain` to the <html> element to prevent horizontal scroll going
		// back in history https://stackoverflow.com/questions/50616221/prevent-page-navigation-on-horizontal-scroll
		// NOTE this won't work in storybook because of the iframe. You can "open canvas in new tab" to see it work in storybook.
		if (document.body && shouldPreventCancelImport) {
			initOverscrollBehaviorX = document.body.style.overscrollBehaviorX ?? '';
			document.body.style.overscrollBehaviorX = 'contain';
		}

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			if (document.body) {
				document.body.style.overscrollBehaviorX = initOverscrollBehaviorX;
			}
		};
	}, [handleKeyDown, shouldPreventCancelImport]);
	return { isCancelImportModalOpen, closeCancelImportModal };
};
